import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Flame, Star } from "lucide-react";
import { PlayerName } from "@/components/PlayerCardDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => `${d.slice(4, 6)}/${d.slice(6, 8)}`;
const short = (m) => (m || "").replace(" MATCH", "").replace("REDUCTION GROUP", "GROUP");

const TonightTop = ({ rows, min }) => (
  <div className="rounded-xl border border-lime-700/40 bg-lime-950/10 p-3" data-testid="num-aligned-tonight-top">
    <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-lime-300">
      <Star className="h-3.5 w-3.5" /> Tonight's aligned skaters · highest aligned-day hit rate
    </p>
    {rows.length ? (
      <div className="flex flex-wrap gap-2">
        {rows.map((r) => (
          <div key={r.player_id} className="rounded-lg border border-lime-700/40 bg-slate-950/60 px-2.5 py-1.5 font-mono text-[10px]" data-testid={`num-top-${r.team}-${r.jersey}`}>
            <span className="font-bold text-cyan-400">#{r.jersey}</span> <PlayerName name={r.name} team={r.team} className="font-semibold uppercase text-slate-100" />
            <span className="ml-1 text-slate-500">{r.team}</span>
            <div className="mt-0.5 text-lime-300"><b>{r.hit_pct}%</b> <span className="text-slate-400">({r.aligned_scoring_nights}/{r.aligned_nights} nights · {r.goals}G) · tonight {short(r.tonight_match)}</span></div>
          </div>
        ))}
      </div>
    ) : <p className="font-mono text-[10px] text-slate-500">No aligned skater tonight has {min}+ aligned nights with a goal in this range yet.</p>}
  </div>
);

export default function NumerologyAlignedScorers({ apiDate }) {
  const [win, setWin] = useState("season");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setD(null); setErr(false);
    axios.get(`${API}/numerology-aligned-scorers`, { params: { window: win, ...(apiDate ? { date: apiDate } : {}) }, timeout: 120000 })
      .then((r) => alive && setD(r.data)).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [apiDate, win]);

  return (
    <div className="space-y-3" data-testid="num-aligned-scorers">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1.5">
          {[["season", "Season"], ["last7", "Last 7 Days"]].map(([k, l]) => (
            <button key={k} data-testid={`num-aligned-window-${k}`} onClick={() => setWin(k)}
              className={`rounded-lg px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${win === k ? "bg-lime-500/20 text-lime-300 shadow-[inset_0_0_0_1px_rgba(163,230,53,0.45)]" : "border border-slate-800 text-slate-500 hover:text-slate-300"}`}>{l}</button>
          ))}
        </div>
        {d && <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500" data-testid="num-aligned-summary">
          {d.scorers.length} aligned-day scorers · {d.nights} graded nights · {d.aligned_player_nights} aligned skater-nights
        </span>}
      </div>
      {err ? <p className="py-8 text-center font-mono text-[11px] text-rose-400" data-testid="num-aligned-error">Aligned scorers unavailable</p>
        : !d ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-fuchsia-400" /></div>
        : (<>
          <TonightTop rows={d.tonight_top} min={d.min_nights_for_top} />
          {d.scorers.length ? (
            <div className="overflow-x-auto rounded-xl border border-slate-800">
              <table className="w-full min-w-[640px] font-mono text-[11px]">
                <thead className="bg-slate-900/60 text-[9px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Player</th><th className="px-2 py-1.5 text-left">Team</th>
                    <th className="px-2 py-1.5 text-right">Goals</th><th className="px-2 py-1.5 text-right">Scored / Aligned nights</th>
                    <th className="px-2 py-1.5 text-right">Hit %</th><th className="px-2 py-1.5 text-left">Aligned scoring nights</th>
                  </tr>
                </thead>
                <tbody>
                  {d.scorers.map((r) => (
                    <tr key={r.player_id} className={`border-t border-slate-800/60 ${r.tonight_match ? "bg-lime-500/5" : ""}`} data-testid={`num-aligned-row-${r.team}-${r.jersey}`}>
                      <td className="whitespace-nowrap px-2 py-1.5">
                        <span className="text-cyan-400">#{r.jersey}</span> <PlayerName name={r.name} team={r.team} className="font-semibold uppercase text-slate-100" />
                        <span className="ml-1 text-[9px] text-slate-500">{r.pos}</span>
                        {r.tonight_match && <span className="ml-1.5 rounded bg-lime-600 px-1 text-[8px] font-bold text-slate-950" title={r.tonight_match}><Flame className="mr-0.5 inline h-2.5 w-2.5" />ALIGNED TONIGHT</span>}
                      </td>
                      <td className="px-2 py-1.5 text-slate-300">{r.team}</td>
                      <td className="px-2 py-1.5 text-right font-bold text-rose-300">{r.goals}</td>
                      <td className="px-2 py-1.5 text-right text-slate-300">{r.aligned_scoring_nights}/{r.aligned_nights}</td>
                      <td className="px-2 py-1.5 text-right font-bold text-lime-300">{r.hit_pct}%</td>
                      <td className="px-2 py-1.5 text-slate-400">{r.nights.map((n) => `${fmt(n.date)} ${n.goals}G ${short(n.match)}`).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="py-8 text-center font-mono text-[11px] text-slate-600" data-testid="num-aligned-empty">No aligned-day goals in this range yet</p>}
        </>)}
    </div>
  );
}
